<template>
  <div v-if="checkLists.length === 0" class="gw-checklist-wrapper">
    {{ $t('message.GW_STEP_NO_CL') }}
  </div>
  <div v-else class="gw-checklist-wrapper">
    <div v-for="(checkList, clIndex) in checkLists" :key="`${clIndex}-${checkList.id}`" class="checklist-wrapper">
      <EditableLabel v-model="checkList.name" class="gw-checklist-wrapper__title" disabled />
      <div class="gw-checklist-wrapper__checklist-items">
        <GwChecklistItem
          v-for="(item, index) in checkList.items"
          :ref="`clitem${clIndex}-${index}`"
          :key="`${index}-${item.id}`"
          :item="item"
          :index="index"
          :disabled="disabled"
          :current="focusable && focusedClIndex == clIndex && focusedIndex == index"
          :passed="focusedClIndex > clIndex || (focusedClIndex === clIndex && focusedIndex > index)"
          :gw="gw"
          :indicator-initial-date="indicatorInitialDate || gw.date"
          :step-index="stepIndex"
          :focusable="focusable"
          @tick="tickItem(...arguments, clIndex, index)"
          @showPictures="showPictures"
          @click="focusNext(clIndex, index)"
          @edit="$emit('edit')"
        />
      </div>
    </div>
    <!-- Files display -->
    <FileViewer
      v-if="filesHolder && fileViewerVisible"
      ref="fileViewer"
      v-model="filesHolder.files"
      :pathname="fileViewerPathname"
      :editable="fileViewerEditable"
      :index="fileViewerIndex"
      @close="fileViewerVisible = false"
      @input="SET_GW_CL_ITEM_COMMENT({ id: filesHolder.id, gwClItemId: filesHolder.gwClItem, files: $event })"
    />
  </div>
</template>

<script>
/**
 * Component used to setup a list of GW checklists.
 * Allows edition by default of checklist items.
 *
 * @vue-prop {Object} gw - GW object
 * @vue-prop {Number} stepIndex - Number of the current step
 * @vue-prop {Array} checkLists - List of checklists to display
 * @vue-prop {String} indicatorInitialDate - Date of the GW (allows setting the date of the indicator values in items)
 *                             Defaults to gw.date
 * @vue-prop {Boolean} focusable - If true, checklist items can be focused (and scaled)
 * @vue-prop {Boolean} disabled - If true, prevent editing checklist items
 *
 */

import { createNamespacedHelpers } from 'vuex';
// Components
import EditableLabel from '@/components/editable-label.vue';
import FileViewer from '@/components/file-system/file-viewer.vue';
import GwChecklistItem from '@/components/gw-checklist-item.vue';
import { SET_GW_CL_ITEM_COMMENT, TICK_ITEM } from '@/store/modules/gemba-walks/action-types';

const { mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

export default {

  name: 'GwChecklistsWrapper',

  components: {
    EditableLabel,
    FileViewer,
    GwChecklistItem,
  },

  props: {
    gw: {
      type: Object,
      required: true,
    },
    stepIndex: {
      type: Number,
      required: true,
    },
    checkLists: {
      type: Array,
      required: true,
    },
    indicatorInitialDate: {
      type: String,
      default: null,
    },
    focusable: Boolean,
    disabled: Boolean,
  },

  data() {
    return {
      focusedClIndex: 0,
      focusedIndex: 0,
      fileViewerVisible: false,
      filesHolder: null,
      fileViewerIndex: 0,
      fileViewerPathname: 'gwClItems/',
      fileViewerEditable: false,
    };
  },

  watch: {
    stepIndex() {
      // Step changed, reset the checklist items focus to the first one
      this.focusedClIndex = 0;
      this.focusedIndex = 0;
    },
  },

  methods: {
    /**
     * Validate or unvalidate a checklist item. May also set the focus to the next item
     * @param  {Object} item - checklist item object (with an id and an ok params)
     * @param  {Boolean} focusNext - Whether the next item in the list should be focused
     * @param  {Number} clIndex - index of the checklist
     * @param  {Number} index - index of the ticked item
     */
    tickItem(item, focusNext, clIndex, index) {
      if (!this.disabled) {
        this.TICK_ITEM({ id: item.id, ok: item.ok }).then((data) => {
          if (data && data instanceof Object && data.id) {
            this.$emit('edit');
            if (item.ok !== 2 && focusNext) {
              this.focusNext(clIndex, ++index);
            }
          }
        });
      }
    },
    /** Display a fullscreen carousel */
    showPictures(item, pathname = 'gwClItems/', editable = false, addFile = false, index = 0) {
      this.filesHolder = item;
      this.fileViewerVisible = true;
      this.fileViewerIndex = index;
      this.fileViewerPathname = pathname;
      this.fileViewerEditable = editable;
      this.$nextTick(() => {
        if (addFile && !(item.files.length > 0)) {
          this.$refs.fileViewer.addFile();
        }
      });
    },
    /** Focus an item */
    focusNext(clIndex, index) {
      if (isNaN(clIndex)) {
        clIndex = this.focusedClIndex + 1;
      }
      if (this.focusable && clIndex < this.checkLists.length) {
        if (isNaN(index)) {
          index = this.focusedIndex + 1;
        }
        if (index < this.checkLists[clIndex].items.length) {
          this.focusedClIndex = clIndex;
          this.focusedIndex = index;
          this.$emit(
            'focusChange',
            clIndex,
            index,
            this.$refs[`clitem${clIndex}-${index}`][0].$el,
          );
        } else {
          this.focusNext(++clIndex, 0);
        }
      }
    },
    ...gwMapActions({
      SET_GW_CL_ITEM_COMMENT,
      TICK_ITEM,
    }),
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/vars.scss";

.gw-checklist-wrapper {
  &__checklist-items {
    padding-right: .5rem;
  }

  .gw-checklist-item {
    margin-top: .7rem;
  }

  &__title {
    font-weight: 600;
  }
}

</style>
